import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const { LuX, LuUpload, LuCheck, LuLoader2, LuTrash2, LuImages } = LuIcons;

const MediaLibrary = ({ onClose, onSelect }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoading(true);
    const { data } = await supabase.storage.from('images').list('', {
      limit: 300,
      sortBy: { column: 'created_at', order: 'desc' }
    });
    
    if (data) {
      const files = data
        .filter(f => f.name && f.name !== '.emptyFolderPlaceholder')
        .map(f => ({
          name: f.name,
          url: supabase.storage.from('images').getPublicUrl(f.name).data.publicUrl
        }));
      setImages(files);
    }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    
    const newFiles = [];
    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `lib_${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
      const { error } = await supabase.storage.from('images').upload(fileName, file);
      
      if (!error) {
        newFiles.push({
          name: fileName,
          url: supabase.storage.from('images').getPublicUrl(fileName).data.publicUrl
        });
      }
    }
    
    setImages(prev => [...newFiles, ...prev]);
    setSelected(prev => [...prev, ...newFiles.map(f => f.url)]);
    setUploading(false);
  };

  const toggleSelect = (url) => {
    setSelected(prev => prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]);
  };

  const handleDelete = async (e, fileName) => {
    e.stopPropagation();
    if (window.confirm('هل أنت متأكد من حذف هذه الصورة نهائياً من سيرفر الموقع؟ سيتم حذفها من أي إعلان يستخدمها.')) {
      await supabase.storage.from('images').remove([fileName]);
      fetchImages();
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-800">مكتبة وسائط الموقع</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
            <SafeIcon icon={LuX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
          <label className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-medium cursor-pointer transition-colors flex items-center gap-2 shadow-sm">
            {uploading ? <SafeIcon icon={LuLoader2} className="w-5 h-5 animate-spin" /> : <SafeIcon icon={LuUpload} className="w-5 h-5" />}
            {uploading ? 'جاري الرفع...' : 'رفع صور جديدة للمكتبة'}
            <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
          <div className="text-sm text-slate-600 font-bold bg-white px-4 py-2 rounded-xl border border-slate-200">
            تم تحديد: <span className="text-indigo-600">{selected.length}</span> صورة
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-16"><SafeIcon icon={LuLoader2} className="w-10 h-10 text-indigo-500 animate-spin" /></div>
          ) : images.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
              {images.map((img, i) => {
                const isSelected = selected.includes(img.url);
                return (
                  <div 
                    key={i} 
                    onClick={() => toggleSelect(img.url)} 
                    className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer border-4 transition-all group bg-white shadow-sm hover:shadow-md ${isSelected ? 'border-indigo-500' : 'border-transparent hover:border-slate-300'}`}
                  >
                    <img src={img.url} className="w-full h-full object-cover" loading="lazy" />
                    
                    {isSelected && (
                      <div className="absolute inset-0 bg-indigo-500/20 flex items-center justify-center">
                        <div className="bg-indigo-500 text-white rounded-full p-2 shadow-md">
                          <SafeIcon icon={LuCheck} className="w-6 h-6" />
                        </div>
                      </div>
                    )}

                    <button
                      onClick={(e) => handleDelete(e, img.name)}
                      className="absolute top-1 right-1 bg-red-500/90 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-sm"
                      title="حذف نهائي من الموقع"
                    >
                      <SafeIcon icon={LuTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20 text-slate-400">
              <SafeIcon icon={LuImages} className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>لا توجد صور في المكتبة حالياً.</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="px-6 py-3 rounded-xl text-slate-600 hover:bg-slate-100 font-bold transition-colors">إلغاء</button>
          <button 
            onClick={() => { onSelect(selected); onClose(); }} 
            disabled={selected.length === 0} 
            className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-colors disabled:opacity-50 shadow-sm"
          >
            إدراج الصور ({selected.length}) للإعلان
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;